import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { authApi, aiApi, billingApi } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MessageCircle, Target, BookOpen, ArrowRight, CheckCircle2, Sparkles, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import SubscriptionCard from './SubscriptionCard';
import AnnualUpgradeBanner from './AnnualUpgradeBanner';
import TrialBanner from './TrialBanner';
import ReferralCard from './ReferralCard';
import ProfileSetupCard from './ProfileSetupCard';
import ToolsGrid from './ToolsGrid';
import PackageAccessBanner from './PackageAccessBanner';
import InstallToHomeScreenCard from './InstallToHomeScreenCard';
import PushPermissionPrompt from '../components/PushPermissionPrompt';
import { logger } from '../lib/logger';

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(user?.role || '');
  const [sport, setSport] = useState(user?.sport || '');
  const [level, setLevel] = useState(user?.level || '');
  const [saving, setSaving] = useState(false);
  const [usage, setUsage] = useState(null);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    setRole(user?.role || '');
    setSport(user?.sport || '');
    setLevel(user?.level || '');
  }, [user]);

  useEffect(() => {
    aiApi.usage()
      .then(setUsage)
      .catch((err) => logger.error('Failed to load usage:', err));
    billingApi.subscription()
      .then(setSub)
      .catch((err) => logger.error('Failed to load subscription:', err));
  }, []);

  const openPortal = async () => {
    try {
      const { url } = await billingApi.portal();
      window.location.href = url;
    } catch (e) {
      toast.error(e.message || 'Could not open billing portal.');
    }
  };

  const showProfilePrompt = !user?.role;

  const saveProfile = async () => {
    if (!role) {
      toast.error('Please select a role to personalize your AI coach.');
      return;
    }
    setSaving(true);
    try {
      const { user: updated } = await authApi.updateProfile({ role, sport: sport || null, level: level || null });
      setUser(updated);
      toast.success('Profile updated. Your AI coach is now personalized.');
    } catch (e) {
      toast.error(e.message || 'Could not update profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#004C54] font-semibold mb-2">
          Welcome back
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
          {user?.name ? `Hey, ${user.name.split(' ')[0]}.` : 'Hey there.'}
        </h1>
        <p className="text-[15px] text-foreground/70 mt-1">
          What do you want to train today?
        </p>
      </div>

      {/* Phase A: encourage home-screen install for users who reach the
          dashboard via mobile Safari/Chrome. Hides itself in standalone or
          after dismissal. */}
      <InstallToHomeScreenCard />

      {/* Phase A round 2: value-first push permission prompt. Self-gates:
          only shows when permission is still default + push is supported +
          not snoozed/subscribed. */}
      <PushPermissionPrompt />

      {showProfilePrompt && (
        <ProfileSetupCard
          role={role}
          sport={sport}
          level={level}
          onRoleChange={setRole}
          onSportChange={setSport}
          onLevelChange={setLevel}
          onSave={saveProfile}
          saving={saving}
        />
      )}

      {!showProfilePrompt && (
        <div className="flex items-center gap-2 text-sm text-foreground/70" data-testid="profile-summary">
          <CheckCircle2 className="h-4 w-4 text-[#004C54]" />
          <span>
            Personalized for <strong className="text-foreground">{user?.role}</strong>
            {user?.sport ? <> · {user.sport}</> : null}
            {user?.level ? <> · {user.level}</> : null}
          </span>
        </div>
      )}

      <TrialBanner sub={sub} />

      <ToolsGrid />

      {sub && (
        <>
          <PackageAccessBanner sub={sub} />
          <AnnualUpgradeBanner sub={sub} />
          <SubscriptionCard
            sub={sub}
            onUpgrade={() => navigate('/app/pricing')}
            onManage={openPortal}
            onChanged={() => billingApi.subscription().then(setSub).catch(() => {})}
          />
        </>
      )}

      {usage && (
        <Card className="border-border" data-testid="usage-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="font-heading text-base font-bold">Today's free usage</h3>
                <p className="text-xs text-foreground/60 mt-0.5">Resets at midnight UTC.</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { key: 'coach', label: 'AI Coach', icon: MessageCircle },
                { key: 'pregame', label: 'Pre-Game', icon: Target },
                { key: 'journal', label: 'Journal', icon: BookOpen },
              ].map(({ key, label, icon: Icon }) => {
                const u = usage[key] || { used: 0, limit: 0, remaining: 0 };
                const pct = u.limit > 0 ? Math.min(100, Math.round((u.used / u.limit) * 100)) : 0;
                const empty = u.remaining <= 0;
                return (
                  <div key={key} className="bg-[#F6F7F8] rounded-lg p-3" data-testid={`usage-${key}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon className="h-3.5 w-3.5 text-[#004C54]" />
                      <span className="text-xs font-semibold">{label}</span>
                    </div>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className={`text-sm font-bold ${empty ? 'text-destructive' : 'text-foreground'}`}>
                        {u.remaining} <span className="text-foreground/60 font-normal">left</span>
                      </span>
                      <span className="text-[11px] text-foreground/50">{u.used}/{u.limit}</span>
                    </div>
                    <div className="h-1.5 bg-white rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${empty ? 'bg-destructive' : 'bg-[#004C54]'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <ReferralCard />

      {sub?.tier === 'premium' ? (
        <Card className="border-2 border-[#004C54]/30 bg-gradient-to-br from-[#0a0d0e] to-[#0f1416] text-white overflow-hidden" data-testid="premium-book-card">
          <CardContent className="p-5 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <div className="w-12 h-12 rounded-xl bg-[#004C54]/30 border border-[#004C54]/40 flex items-center justify-center shrink-0">
                <Calendar className="h-5 w-5 text-[#4FBCC7]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#4FBCC7] font-semibold mb-1.5">
                  Premium membership
                </div>
                <h3 className="font-heading text-lg sm:text-xl font-bold mb-1">Book a 1:1 with Coach Lewis</h3>
                <p className="text-[14px] text-white/65 leading-relaxed">
                  Your Premium membership includes direct booking. 1:1 sessions are intentionally limited — grab the next open 30-minute slot and bring one focus area to the call.
                </p>
              </div>
              <Button asChild className="bg-[#4FBCC7] hover:bg-[#3aa6b1] text-[#0a0d0e] font-bold h-11 px-5">
                <Link to="/app/book" data-testid="premium-book-cta">
                  Book a session
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-border">
          <CardContent className="p-5 sm:p-6">
            <h3 className="font-heading text-base font-bold mb-1.5">Want to talk to Coach Lewis directly?</h3>
            <p className="text-sm text-foreground/70 mb-4">A 30-minute Discovery Call to see if 1:1 is the right next step for you. By design, limited availability.</p>
            <Button asChild variant="outline" className="border-[#004C54] text-[#004C54] hover:bg-[#004C54] hover:text-white">
              <Link to="/app/book" data-testid="book-consultation">
                Book a Discovery Call
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
